import React, { lazy, Suspense } from 'react';
import SimpleBar from 'simplebar-react';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';
import { ChecklistTask } from '../../data/checklistData';

const ClarityDashboard = lazy(() => import('../charts/ClarityDashboard'));
const ContentTypeChart = lazy(() => import('../charts/ContentTypeChart'));
const YouTubeImpactChart = lazy(() => import('../charts/YouTubeImpactChart'));
const TrafficSourcesChart = lazy(() => import('../charts/TrafficSourcesChart'));

interface PlaybookTaskDialogProps {
  visible: boolean;
  onHide: () => void;
  task: ChecklistTask;
  instructionText: string;
}

type ListItem = { text: string; sub: string[] };
type Block =
  | { kind: 'para'; text: string }
  | { kind: 'heading'; text: string }
  | { kind: 'list'; items: ListItem[] }
  | { kind: 'protip'; text: string };

const isBullet = (s: string) => s.startsWith('•');
const isSubBullet = (s: string) => s.startsWith('–') || s.startsWith('-');
const isProTip = (s: string) => /^pro tip/i.test(s);

/** Parses the multi-line instruction string into renderable blocks: intro paragraphs,
 *  bold section headings, real bullet lists (with sub-bullets) and a "Pro tip" callout. */
const parseInstruction = (text: string): Block[] => {
  const blocks: Block[] = [];
  let list: ListItem[] | null = null;
  const protipLines: string[] = [];
  let inProtip = false;

  const flushList = () => {
    if (list) {
      blocks.push({ kind: 'list', items: list });
      list = null;
    }
  };

  text.split('\n').forEach((raw) => {
    const line = raw.trim();

    if (inProtip) {
      if (line) protipLines.push(line);
      return;
    }
    if (!line) {
      flushList();
      return;
    }
    if (isBullet(line)) {
      if (!list) list = [];
      list.push({ text: line.replace(/^•\s*/, ''), sub: [] });
      return;
    }
    if (isSubBullet(line)) {
      if (list && list.length) list[list.length - 1].sub.push(line.replace(/^[–-]\s*/, ''));
      else {
        flushList();
        blocks.push({ kind: 'para', text: line });
      }
      return;
    }
    if (line.endsWith(':')) {
      flushList();
      if (isProTip(line)) inProtip = true;
      else blocks.push({ kind: 'heading', text: line });
      return;
    }
    flushList();
    blocks.push({ kind: 'para', text: line });
  });

  flushList();
  if (protipLines.length) blocks.push({ kind: 'protip', text: protipLines.join('\n') });
  return blocks;
};

/** Instruction popup: a formatted, readable rendering of a task's instruction. */
const PlaybookTaskDialog: React.FC<PlaybookTaskDialogProps> = ({
  visible,
  onHide,
  task,
  instructionText,
}) => {
  const { t } = useTranslation();
  const blocks = parseInstruction(instructionText);
  const bodyBlocks = blocks.filter((b) => b.kind !== 'protip');
  const protipBlocks = blocks.filter((b): b is Extract<Block, { kind: 'protip' }> => b.kind === 'protip');

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={
        <div className="playbook-task-dialog__header">
          <div className="playbook-task-dialog__title">
            {t(task.titleKey, { defaultValue: task.defaultTitle })}
          </div>
          {task.descriptionKey && (
            <p className="playbook-task-dialog__description">
              {t(task.descriptionKey, { defaultValue: task.defaultDescription })}
            </p>
          )}
        </div>
      }
      className="dialog-box playbook-task-dialog"
      draggable={false}
      style={{ width: '640px' }}
    >
      <SimpleBar style={{ maxHeight: '65vh' }} autoHide={false}>
        <div className="playbook-task-dialog__content">
          {bodyBlocks.map((block, i) => {
            if (block.kind === 'heading') {
              return (
                <div key={i} className="playbook-task-dialog__heading">
                  {block.text}
                </div>
              );
            }
            if (block.kind === 'list') {
              return (
                <ul key={i} className="playbook-task-dialog__list">
                  {block.items.map((item, j) => (
                    <li key={j}>
                      {item.text}
                      {item.sub.length > 0 && (
                        <ul className="playbook-task-dialog__sublist">
                          {item.sub.map((s, k) => (
                            <li key={k}>{s}</li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              );
            }
            return (
              <p key={i} className="playbook-task-dialog__para">
                {block.text}
              </p>
            );
          })}

          {task.instructionChart && (
            <div className="playbook-task-dialog__chart">
              <Suspense fallback={<div className="playbook-task-dialog__chart-placeholder" />}>
                {task.instructionChart === 'clarity' && <ClarityDashboard />}
                {task.instructionChart === 'contentType' && <ContentTypeChart />}
                {task.instructionChart === 'youtubeImpact' && <YouTubeImpactChart />}
                {task.instructionChart === 'trafficSources' && <TrafficSourcesChart />}
              </Suspense>
            </div>
          )}

          {/* Pro tip always rendered last. */}
          {protipBlocks.map((block, i) => (
            <div key={i} className="playbook-protip">
              <span className="material-symbols-outlined playbook-protip__icon">lightbulb</span>
              <div className="playbook-protip__text">{block.text}</div>
            </div>
          ))}
        </div>
      </SimpleBar>
    </Dialog>
  );
};

export default PlaybookTaskDialog;
